/**
 * PublishAdapter interface for the Finance Automation Manager.
 *
 * The publish step is the final stage in the lifecycle: a document that has
 * passed review and approvals is "published" to a target accounting system.
 *
 * The GLPublishAdapter wraps the unified approval/posting API:
 *   - /api/v1/work-items/documents/approve (markReadyToSend: true for sales, approved: true for expenses)
 * After posting, the DIN work_item row is updated to status='completed'.
 */

import type { FinanceWorkspace } from './lifecycle';

// --- Publishable document types ----------------------------------------------

/**
 * Work item types that have an external-accounting equivalent and may be
 * published to an adapter (QBO, Xero, Sage, D365).
 *
 * Internal-only document types — e.g. work_order, sales_order, delivery_note —
 * have no external entity (QBO has no work-order object) and are managed
 * entirely inside TotalAccess. Every publish path (approval-triggered publish,
 * auto-publish crons, manual publish endpoints, bulk publish) must gate on
 * this list so internal documents can never be mis-posted to an external
 * system as Invoices, Bills or Purchases.
 */
export const EXTERNAL_PUBLISHABLE_ITEM_TYPES: ReadonlySet<string> = new Set([
  // Sales documents → QBO Invoice / Estimate / CreditMemo
  'invoice', 'quote', 'proforma', 'credit_note',
  // Purchases → QBO Bill / Purchase / PurchaseOrder / VendorCredit
  'supplier_invoice', 'supplier_credit', 'purchase_order', 'purchase',
  // Expenses → QBO Purchase (or Bill when payment terms apply).
  // Expense-family types may appear as item_type directly (fuel, toll, …)
  // or as item_subtype on a generic 'expense'.
  'expense', 'fuel', 'toll', 'maintenance', 'breakdown',
  'casual_wage', 'vehicle_expense',
]);

/**
 * Document types that are internal-only — they have no external-accounting
 * entity and must never be sent to an external adapter. Checked against
 * PublishDocument.documentType in endpoints that accept caller-supplied
 * payloads (where documentType may be a subtype or a work-item type).
 */
export const INTERNAL_ONLY_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'work_order',
  'sales_order',
  'delivery_note',
  'waybill',
  'rma',
  'quote_request',
  'billing_submission',
  'grn',
  'supplier_quote',
  'supplier_delivery_note',
  'purchase_requisition',
  'rfq',
  'purchase_return',
  'picking_slip',
  'packing_slip',
  'stock_adjustment',
  'transfer_order',
  'debit_note',
  'din',
]);

// --- Currency helpers --------------------------------------------------------

export function resolvePublishCurrency(doc: PublishDocument): string {
  return resolvePublishCurrencyWithWarning(doc).currency;
}

/**
 * Resolve the publish currency and indicate whether the fallback to ZAR was
 * used. Callers that can surface warnings (e.g. publish adapters) should use
 * this variant and add the warning to PublishResult.warnings so the user knows
 * the currency was defaulted rather than explicitly set.
 */
export function resolvePublishCurrencyWithWarning(doc: PublishDocument): { currency: string; fallbackUsed: boolean } {
  const raw = String(doc.currency || doc.payload.currency || doc.payload.base_currency || 'ZAR').toUpperCase();
  if (raw.length === 3 && /^[A-Z]{3}$/.test(raw)) {
    return { currency: raw, fallbackUsed: false };
  }
  console.warn(
    `[publishAdapter] Invalid currency code "${raw}" for document ${doc.workspace}:${doc.documentId} — defaulting to ZAR`,
  );
  return { currency: 'ZAR', fallbackUsed: true };
}

export function extractFxContext(doc: PublishDocument): { currency: string; baseCurrency: string | null; rate: number | null; baseAmount: number | null } | null {
  const fxContext = doc.payload.fx_context as Record<string, unknown> | undefined;
  if (!fxContext || typeof fxContext !== 'object') return null;
  return {
    currency: String(fxContext.currency || resolvePublishCurrency(doc)),
    baseCurrency: fxContext.base_currency ? String(fxContext.base_currency) : null,
    rate: fxContext.rate != null ? Number(fxContext.rate) : null,
    baseAmount: fxContext.base_amount != null ? Number(fxContext.base_amount) : null,
  };
}

// --- Types -------------------------------------------------------------------

export interface PublishDocument {
  /** Workspace that owns this document. */
  workspace: FinanceWorkspace;
  /** Workspace-specific document identifier (e.g. field_action_id, finance_document_id). */
  documentId: string;
  /** Document type within the workspace (e.g. receipt, supplier_invoice, bank_statement). */
  documentType: string;
  /** Extracted/parsed payload from the review stage. */
  payload: Record<string, unknown>;
  /** Matched contact (supplier/customer) id, if any. */
  matchedContactId?: string | number | null;
  /** Client id for multi-tenant routing. */
  clientId: number;
  /** References produced by the intake pipeline (e.g. expense_id, finance_document_id). */
  producedRef?: Record<string, unknown> | null;
  /** Explicit currency override (ISO 4217). If not set, resolved from payload. */
  currency?: string;
  /** User who approved this document — used for segregation-of-duties checks. */
  approvedByUserId?: number | null;
  /** Adapter-specific publish options (e.g. { xeroStatus: 'AUTHORISED' }). */
  adapterOptions?: Record<string, unknown> | null;
}

export interface PublishResult {
  success: boolean;
  /** Target-system reference (e.g. GL journal entry id, Xero invoice id). */
  externalRef?: string;
  error?: string;
  /** Warnings that don't prevent publish but should be surfaced. */
  warnings?: string[];
  /** File content for file-based adapters (e.g. CSV export). */
  content?: string;
  /** MIME type of the content (e.g. 'text/csv'). */
  contentType?: string;
  /** Suggested filename for download. */
  filename?: string;
  /** Retry-After hint (seconds) from 429 responses, for bulk publish rate limiting (§9). */
  retryAfter?: number;
}

// --- Publish Preview ---------------------------------------------------------

export interface PublishPreviewLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  account_code?: string;
  tax_type?: string;
}

export interface PublishPreview {
  documentId: string;
  workspace: FinanceWorkspace;
  documentType: string;
  /** Target adapter id (e.g. 'gl', 'xero', 'quickbooks', 'sage'). */
  adapterId: string;
  adapterLabel: string;
  /** Vendor/customer name. */
  contactName: string;
  /** Document date (ISO date string). */
  date: string;
  /** Currency code (ISO 4217). */
  currency: string;
  /** FX rate if multi-currency, else null. */
  fxRate?: number | null;
  /** Line items that will be posted. */
  lineItems: PublishPreviewLineItem[];
  /** Total amount to be posted. */
  totalAmount: number;
  /** Tax amount if known. */
  taxAmount?: number;
  /** Any validation warnings. */
  warnings: string[];
  /** Whether the document is ready to publish. */
  canPublish: boolean;
  /** Blocking errors that prevent publishing. */
  blockingErrors: string[];
}

// --- Batch Publishing --------------------------------------------------------

export interface BatchPublishRequest {
  /** Documents to publish. */
  documents: PublishDocument[];
  /** Target adapter id. If not set, uses the default adapter per document. */
  adapterId?: string;
  /** Whether to stop on first error or continue publishing remaining docs. */
  stopOnError?: boolean;
  /** Whether to generate previews before publishing. */
  previewFirst?: boolean;
}

export interface BatchPublishResultItem {
  documentId: string;
  success: boolean;
  /** True if the document was skipped (not attempted) due to preview validation. Consumers should check this before treating success:false as a failure. */
  skipped?: boolean;
  externalRef?: string;
  error?: string;
  warnings?: string[];
  /** Duration in milliseconds. */
  durationMs?: number;
  /** File content for file-based adapters (e.g. CSV export). */
  content?: string;
  /** MIME type of the content. */
  contentType?: string;
  /** Suggested filename for download. */
  filename?: string;
}

export interface BatchPublishResult {
  success: boolean;
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
  results: BatchPublishResultItem[];
  /** Overall duration in milliseconds. */
  totalDurationMs: number;
}

// --- Adapter interface -------------------------------------------------------

/**
 * Payload for publishing a journal entry to an external accounting system.
 * Used by inventory GL postings, depreciation runs, and other ERP journal entries.
 */
export interface JournalEntryPublishPayload {
  /** Client id for multi-tenant routing. */
  clientId: number;
  /** Journal entry number / reference (from journal_entries.entry_number). */
  entryNumber: string;
  /** Journal entry ID in our system (for traceability). */
  entryId?: number | string;
  /** Posting date (ISO string or YYYY-MM-DD). */
  date: string;
  /** Description / narration for the journal entry. */
  description: string;
  /** Source type (e.g. 'inventory', 'depreciation', 'asset_disposal'). */
  sourceType?: string;
  /** Reference (e.g. PO number, GRN number). */
  reference?: string;
  /** Journal lines — must be balanced (total debits = total credits). */
  lines: Array<{
    /** GL account code in the external system. */
    accountCode: string;
    /** Line description. */
    description?: string;
    /** Debit amount (0 if credit). */
    debit: number;
    /** Credit amount (0 if debit). */
    credit: number;
    /** Optional cost center / department code. */
    costCenterCode?: string;
  }>;
  /** Currency code (ISO 4217). Defaults to ZAR. */
  currency?: string;
  /** If this is a reversing entry, the external ref of the original JE. */
  reversalOfExternalRef?: string;
}

// ---------------------------------------------------------------------------
// Phase 10: Budget sync payloads
// ---------------------------------------------------------------------------

export interface BudgetPublishPayload {
  /** Client id for multi-tenant routing. */
  clientId: number;
  /** Budget period ID in our system. */
  budgetPeriodId: number;
  /** Budget name (e.g. "FY2026 Operating Budget"). */
  name: string;
  /** Fiscal year (e.g. 2026). */
  fiscalYear: number;
  /** Start month (1-12). */
  startMonth: number;
  /** End month (1-12). */
  endMonth: number;
  /** Budget lines — one per account per month. */
  lines: Array<{
    /** GL account code in the external system. */
    accountCode: string;
    /** Month index (1-12). */
    monthIndex: number;
    /** Budgeted amount for this account in this month. */
    amount: number;
  }>;
  /** Currency code (ISO 4217). Defaults to ZAR. */
  currency?: string;
}

export interface BudgetActualsResult {
  success: boolean;
  /** Actual amounts per account per month. */
  actuals?: Array<{
    accountCode: string;
    accountName?: string;
    monthIndex: number;
    budgetAmount: number;
    actualAmount: number;
    variance: number;
  }>;
  error?: string;
}

/** Local contact fields needed to create/update an external accounting contact. */
export interface ContactSyncPayload {
  /** Internal contacts.id. */
  id: number;
  /** 'customer' → QBO Customer / Xero customer; 'supplier' → QBO Vendor / Xero supplier. */
  contactType: 'customer' | 'supplier';
  /** Display name — required by most external systems (QBO DisplayName). */
  displayName: string;
  companyName?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  taxNumber?: string | null;
  address?: {
    line1?: string | null;
    city?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  /** Net days (0 = COD / due on receipt). Best-effort mapped to the external term. */
  paymentTermsDays?: number | null;
}

export interface PublishAdapter {
  /** Unique adapter identifier (e.g. 'gl', 'xero', 'quickbooks', 'sage'). */
  id: string;
  /** Human-readable label for the publish target selector. */
  label: string;
  /** Whether this adapter is currently configured and ready (global, advisory). */
  isConfigured: boolean;
  /** Per-tenant configured check. Falls back to `isConfigured` if not implemented. */
  isConfiguredForClient?(clientId: number): boolean;
  /** Publish a document to this target. */
  publish(doc: PublishDocument): Promise<PublishResult>;
  /** Validate that a document is ready for publishing (pre-flight check). */
  canPublish(doc: PublishDocument): boolean;
  /** Publish a payment against an existing invoice/bill (optional — not all adapters support this). */
  publishPayment?(doc: PublishDocument): Promise<PublishResult>;
  /** Publish a journal entry (inventory GL, depreciation, etc.) to the external system. */
  publishJournalEntry?(payload: JournalEntryPublishPayload): Promise<PublishResult>;
  /** Publish a credit note (supplier credit or sales credit) to the external system. */
  publishCreditNote?(doc: PublishDocument): Promise<PublishResult>;
  /** Publish a write-off (bad debt) to the external system as a CreditMemo. */
  publishWriteOff?(doc: PublishDocument): Promise<PublishResult>;
  /** Publish a source document attachment to an existing external document. */
  publishAttachment?(doc: PublishDocument, externalRef: string): Promise<PublishResult>;
  /** Update an already-published document in the external system (§3.5.4). */
  updateDocument?(doc: PublishDocument, externalRef: string): Promise<PublishResult>;
  /**
   * Push a local contact to the external system — creates it when
   * `externalContactId` is absent, updates it otherwise. Returns the external
   * contact id in `externalRef`. Optional — adapters that don't support it are
   * skipped by the contact push-on-edit sweep.
   */
  syncContact?(clientId: number, contact: ContactSyncPayload, externalContactId?: string | null): Promise<PublishResult>;
  /** Fetch the last-modified timestamp of an external document (e.g. QBO
   * MetaData.LastUpdatedTime). Used by the push-on-edit sweep to detect
   * both-sides-changed before overwriting an external edit. Returns null
   * when unknown or unsupported. */
  fetchDocumentTimestamp?(externalRef: string, doc: PublishDocument): Promise<Date | null>;
  /** Publish a quote/estimate to the external system (QBO Estimate, Xero Quote). */
  publishQuote?(doc: PublishDocument): Promise<PublishResult>;
  /** Publish a purchase order to the external system (QBO PurchaseOrder). */
  publishPurchaseOrder?(doc: PublishDocument): Promise<PublishResult>;
  /** Publish a sales order to the external system (D365 BC salesOrders). */
  publishSalesOrder?(doc: PublishDocument): Promise<PublishResult>;
  /** Void/delete a previously-published document in the external system. */
  voidDocument?(externalRef: string, docType: 'invoice' | 'bill' | 'estimate' | 'purchase_order' | 'credit_memo' | 'purchase' | 'sales_order', clientId: number): Promise<PublishResult>;
  /** Publish a budget to the external system (QBO Budget with monthly lines). */
  publishBudget?(payload: BudgetPublishPayload): Promise<PublishResult>;
  /** Fetch actuals from the external system for a published budget (variance analysis). */
  fetchBudgetActuals?(externalRef: string, clientId: number): Promise<BudgetActualsResult>;
  /** Fetch budgets from the external system for import. */
  fetchBudgets?(clientId: number): Promise<{ success: boolean; budgets?: Array<{ externalId: string; name: string; fiscalYear: number; startMonth: string; endMonth: string; lines: Array<{ accountCode: string; monthIndex: number; amount: number }> }>; error?: string }>;
  /** Fetch a ProfitAndLoss report from the external system for actuals. */
  fetchProfitAndLoss?(clientId: number, startDate: string, endDate: string): Promise<{ success: boolean; rows?: Array<{ accountCode: string; accountName: string; amount: number; section: string }>; error?: string }>;
  /** Fetch all customers/vendors from the external system for contact mapping. */
  fetchExternalContacts?(clientId: number): Promise<ExternalContact[]>;
  /** Fetch all accounts from the external system for chart-of-accounts mapping. */
  fetchExternalAccounts?(clientId: number): Promise<ExternalAccount[]>;
  /** Fetch trial balance from the external system for reconciliation (Phase 5). */
  fetchTrialBalance?(clientId: number, asOfDate: string): Promise<ExternalTrialBalanceLine[]>;
}

export interface ExternalContact {
  /** External system entity ID (e.g. QBO Customer Id). */
  id: string;
  /** Display name / company name. */
  name: string;
  /** Contact type: 'customer' | 'vendor' | 'other'. */
  type: 'customer' | 'vendor' | 'other';
  /** Email if available. */
  email?: string | null;
  /** Phone if available. */
  phone?: string | null;
  /** Whether the contact is active in the external system. */
  active?: boolean;
  /** Outstanding balance in the external system (Phase 5 reconciliation). */
  balance?: number | null;
}

/** Trial balance line from the external system (Phase 5 reconciliation). */
export interface ExternalTrialBalanceLine {
  /** External account code or ID. */
  externalCode: string;
  /** Account name. */
  accountName: string;
  /** Debit amount. */
  debit: number;
  /** Credit amount. */
  credit: number;
  /** Net balance (debit - credit). */
  balance: number;
}

export interface ExternalAccount {
  /** External system account ID (e.g. QBO Account Id). */
  id: string;
  /** Account number / code in the external system. */
  accountNumber?: string | null;
  /** Account name. */
  name: string;
  /** Account type (e.g. 'Asset', 'Liability', 'Revenue', 'Expense'). */
  accountType?: string | null;
  /** Account subtype if available. */
  accountSubType?: string | null;
  /** Whether the account is active. */
  active?: boolean;
}

// --- GL Publish Adapter ------------------------------------------------------
//
// Wraps the existing GL posting path by calling the unified approval/posting API
// that DocumentApprovalBoard uses, then marking the DIN work_item row as 'completed'.

interface GLPublishTokenAccessor {
  getTokens(): { access_token: string | null } | null;
}

export class GLPublishAdapter implements PublishAdapter {
  id = 'gl';
  label = 'General Ledger';
  isConfigured = true;
  private tokenAccessor: GLPublishTokenAccessor | null = null;
  /**
   * Base URL for internal API calls. Empty string = relative URLs (browser
   * context). When running server-side (Node.js / server actions), this must
   * be set to an absolute URL since fetch() requires absolute URLs in Node.
   */
  private baseUrl: string = '';

  /** Inject a token accessor (e.g. authService) so the adapter can authenticate API calls. */
  setTokenAccessor(accessor: GLPublishTokenAccessor): void {
    this.tokenAccessor = accessor;
  }

  /**
   * Set the base URL for internal API calls. Required for server-side usage
   * where fetch() needs absolute URLs. In browser context, leave as '' to
   * use relative URLs.
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url.replace(/\/$/, '');
  }

  private getToken(): string | null {
    return this.tokenAccessor?.getTokens()?.access_token ?? null;
  }

  async publish(doc: PublishDocument): Promise<PublishResult> {
    const token = this.getToken();
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    // Copy so we don't mutate the caller's object (e.g. in batch retries)
    const producedRef: Record<string, unknown> = { ...(doc.producedRef || {}) };
    const warnings: string[] = [];
    let externalRef: string | undefined;
    let postedToGl = false;

    try {
      // Idempotency check: if the document is already published, return early
      if (producedRef.published_ref) {
        return { success: true, externalRef: String(producedRef.published_ref), warnings: ['Already published — skipping duplicate'] };
      }

      // Publishing must never materialize — EXP/BIL creation happens only on
      // approval in DocumentModal (document-review 'materialize'). A document
      // without a produced_ref was never approved; warn and let the workspace
      // branches below record the intake log without posting to the GL.
      if (!producedRef.expense_id && !producedRef.finance_document_id && !producedRef.field_action_id) {
        warnings.push('Document not materialized — approve it in DocumentModal before publishing');
      }

      if (doc.workspace === 'costs') {
        const expenseId = producedRef.expense_id ?? producedRef.field_action_id;
        if (expenseId) {
          const res = await fetch(`${this.baseUrl}/api/v1/work-items/documents/approve`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ documentId: expenseId, approved: true, markReadyToSend: false }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { success: false, error: err?.error || `Failed to approve expense (${res.status})` };
          }
          const data = await res.json().catch(() => ({}));
          externalRef = data?.data?.id ?? String(expenseId);
          postedToGl = true;
        } else {
          warnings.push('No expense_id in produced_ref — posting intake log only');
        }
      } else if (doc.workspace === 'sales') {
        const financeDocId = producedRef.finance_document_id;
        if (financeDocId) {
          const res = await fetch(`${this.baseUrl}/api/v1/work-items/documents/approve`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ documentId: financeDocId, approved: true, markReadyToSend: true }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { success: false, error: err?.error || `Failed to approve document (${res.status})` };
          }
          const data = await res.json().catch(() => ({}));
          externalRef = data?.data?.id ?? String(financeDocId);
          postedToGl = true;
        } else {
          warnings.push('No finance_document_id in produced_ref — posting intake log only');
        }
      }

      // Only mark the intake log as 'published' if something was actually
      // posted to the GL. Otherwise the document would appear "published" with
      // no corresponding journal entry, which is a data-integrity issue.
      if (postedToGl) {
        const patchRes = await fetch(`${this.baseUrl}/api/v1/finance/document-review`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: doc.documentId, action: 'publish' }),
        });
        if (!patchRes.ok) {
          warnings.push('Failed to update intake log status to published');
        }
      } else {
        warnings.push('Document not posted to GL — intake log left in current status. Resolve the warnings above and retry.');
        return { success: false, error: 'Nothing was posted to the GL — see warnings', warnings };
      }

      return { success: true, externalRef, warnings: warnings.length > 0 ? warnings : undefined };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Unknown GL publish error' };
    }
  }

  canPublish(doc: PublishDocument): boolean {
    return Boolean(doc.documentId && doc.clientId);
  }
}

// --- Adapter registry --------------------------------------------------------

const adapterRegistry = new Map<string, PublishAdapter>();

export function registerPublishAdapter(adapter: PublishAdapter): void {
  adapterRegistry.set(adapter.id, adapter);
}

export function getPublishAdapter(id: string): PublishAdapter | undefined {
  return adapterRegistry.get(id);
}

export function listPublishAdapters(): PublishAdapter[] {
  return Array.from(adapterRegistry.values());
}

export function unregisterPublishAdapter(id: string): void {
  adapterRegistry.delete(id);
}

// --- External Accounting Adapters (Phase 6) ----------------------------------
//
// Each external adapter follows the same PublishAdapter interface.
// They are registered on demand (when the user configures a connection)
// rather than at module load, because they need credentials.

export interface ExternalAdapterConfig {
  /** Adapter id (e.g. 'xero', 'quickbooks', 'sage'). */
  id: string;
  /** Human-readable label. */
  label: string;
  /** OAuth redirect URL or API base URL, depending on adapter. */
  apiBaseUrl: string;
  /** OAuth authorization URL (if applicable). */
  authUrl?: string;
  /** Token URL for OAuth exchange. */
  tokenUrl?: string;
  /** Scopes required for the adapter. */
  scopes?: string[];
  /** Whether the adapter is currently connected (has valid credentials). */
  connected: boolean;
  /** Tenant that owns this config (server-side only, never sent to browser). */
  clientId?: number;
  /** Stored credentials (encrypted at rest by the caller).
   * For OAuth adapters: accessToken/refreshToken are OAuth tokens.
   * For api_key adapters (e.g. Sage ZA): accessToken = API key,
   * refreshToken = base64(username:password) for Basic Auth,
   * tenantId = provider-specific context (e.g. Company ID). */
  credentials?: {
    accessToken?: string;
    refreshToken?: string;
    tenantId?: string;
    expiresAt?: string;
  };
  /** Fiscal year start month (1-12). Defaults to 1 (January). Used by budget sync. */
  fiscalYearStartMonth?: number;
}

/** Safe adapter config for API responses — credentials stripped. */
export interface SafeAdapterConfig {
  id: string;
  label: string;
  apiBaseUrl: string;
  authUrl?: string;
  tokenUrl?: string;
  scopes?: string[];
  connected: boolean;
  tenantId?: string;
  expiresAt?: string;
  /** Last successful sync timestamp (read-only, safe to expose). */
  lastSyncedAt?: string | null;
  /** Last sync error message (read-only, safe to expose). */
  lastError?: string | null;
}

/** Strip credentials from an adapter config for safe API responses. */
export function sanitizeAdapterConfig(config: ExternalAdapterConfig & { lastSyncedAt?: string | Date | null; lastError?: string | null }): SafeAdapterConfig {
  return {
    id: config.id,
    label: config.label,
    apiBaseUrl: config.apiBaseUrl,
    authUrl: config.authUrl,
    tokenUrl: config.tokenUrl,
    scopes: config.scopes,
    connected: config.connected,
    tenantId: config.credentials?.tenantId,
    expiresAt: config.credentials?.expiresAt,
    lastSyncedAt: config.lastSyncedAt ? String(config.lastSyncedAt) : null,
    lastError: config.lastError ?? null,
  };
}

/**
 * In-memory store of external adapter configs, keyed by `${clientId}:${adapterId}`
 * to prevent cross-tenant credential leakage. Falls back to adapter-id-only key
 * when clientId is not set (backward compat for client-side calls).
 */
const externalConfigs = new Map<string, ExternalAdapterConfig>();
/** Parallel map tracking when each config was cached, for TTL-based invalidation. */
const configCacheTimes = new Map<string, number>();
/** Cache TTL in ms. In multi-process (PM2) setups, another process may update
 * the DB without notifying this process. Stale entries expire after this interval
 * so callers fall through to the DB-backed store (adapterConfigStore.ts). */
const CONFIG_CACHE_TTL_MS = 60_000;

function configKey(id: string, clientId?: number): string {
  return clientId != null ? `${clientId}:${id}` : id;
}

function isCacheEntryFresh(key: string): boolean {
  const cachedAt = configCacheTimes.get(key);
  if (!cachedAt) return false;
  return Date.now() - cachedAt < CONFIG_CACHE_TTL_MS;
}

export function getExternalAdapterConfig(id: string, clientId?: number): ExternalAdapterConfig | undefined {
  if (clientId != null) {
    const tenantKey = configKey(id, clientId);
    if (isCacheEntryFresh(tenantKey)) {
      const tenantConfig = externalConfigs.get(tenantKey);
      if (tenantConfig) return tenantConfig;
    } else {
      // Stale — evict so the next load comes from DB
      externalConfigs.delete(tenantKey);
      configCacheTimes.delete(tenantKey);
    }
  }
  // Fall back to adapter-id-only key (backward compat for client-side display)
  if (isCacheEntryFresh(id)) {
    return externalConfigs.get(id);
  }
  externalConfigs.delete(id);
  configCacheTimes.delete(id);
  return undefined;
}

/**
 * Set an external adapter config in the in-memory cache.
 * The DB-backed store (adapterConfigStore.ts) is the source of truth;
 * this cache is populated on load and updated on save.
 *
 * Tenant-scoped configs (clientId set) are ONLY stored under the
 * `${clientId}:${id}` key — never under the adapter-id-only key — to
 * prevent cross-tenant credential leakage in multi-tenant environments.
 * The adapter-id-only key is reserved for global/non-tenant configs.
 */
export function setExternalAdapterConfig(config: ExternalAdapterConfig): void {
  if (config.clientId != null) {
    const tenantKey = configKey(config.id, config.clientId);
    externalConfigs.set(tenantKey, config);
    configCacheTimes.set(tenantKey, Date.now());
  } else {
    // Only set adapter-id-only key for configs without a clientId (global configs)
    externalConfigs.set(config.id, config);
    configCacheTimes.set(config.id, Date.now());
  }
}

export function listExternalAdapterConfigs(): ExternalAdapterConfig[] {
  // Return unique configs (deduplicate by adapter id, prefer tenant-scoped)
  const byAdapterId = new Map<string, { config: ExternalAdapterConfig; key: string }>();
  for (const [key, config] of externalConfigs) {
    if (!isCacheEntryFresh(key)) {
      externalConfigs.delete(key);
      configCacheTimes.delete(key);
      continue;
    }
    // Prefer tenant-scoped configs (key contains ':') over adapter-id-only keys.
    // A tenant-scoped config always wins over a global one, regardless of
    // iteration order, to prevent cross-tenant credential leakage.
    // However, a tenant-scoped config must NOT overwrite another tenant-scoped
    // config for the same adapter — that would leak one tenant's credentials
    // to another tenant's call.
    const existing = byAdapterId.get(config.id);
    if (!existing || (!existing.key.includes(':') && key.includes(':'))) {
      byAdapterId.set(config.id, { config, key });
    }
  }
  return Array.from(byAdapterId.values()).map((entry) => entry.config);
}

export function removeExternalAdapterConfig(id: string, clientId?: number): void {
  if (clientId != null) {
    const tenantKey = configKey(id, clientId);
    externalConfigs.delete(tenantKey);
    configCacheTimes.delete(tenantKey);
  }
  externalConfigs.delete(id);
  configCacheTimes.delete(id);
}

/** Clear all cached adapter configs. Useful when the DB store is reloaded. */
export function clearExternalAdapterConfigCache(): void {
  externalConfigs.clear();
  configCacheTimes.clear();
}

// --- Token refresher / contact resolver types --------------------------------
//
// These types are exported for the server-only externalPublishAdapters.ts module.
// The actual implementations (setTokenRefresher, setContactResolver, and the
// external adapter classes) live in externalPublishAdapters.ts to prevent
// OAuth token handling code from being bundled into the browser.

export type TokenRefresherFn = (
  clientId: number,
  adapterId: string,
) => Promise<ExternalAdapterConfig | null>;

export type ContactResolverFn = (
  clientId: number,
  contactId: string | number,
  adapterId: string,
) => Promise<string | null>;

// --- Adapter metadata for UI rendering ---------------------------------------

export interface AdapterMeta {
  id: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  /** Path to SVG logo in /public, used instead of emoji icon when available. */
  logoPath?: string;
  authType: 'oauth2' | 'api_key';
  apiBaseUrl: string;
  authUrl?: string;
  tokenUrl?: string;
  scopes?: string[];
  docsUrl: string;
  /** Whether the provider supports PKCE (code_challenge/code_verifier).
   * Defaults to true when omitted. Sage Business Cloud Accounting does not
   * document PKCE support, so it is set to false for the Sage adapter. */
  supportsPkce?: boolean;
  /** Whether the adapter supports inbound sync (pulling data FROM the
   * accounting system). Defaults to true. Set to false for adapters that
   * are publish-only (e.g. Sage ZA, Dynamics 365 — no inbound fetchers
   * implemented yet). */
  supportsInboundSync?: boolean;
}

export const EXTERNAL_ADAPTER_METAS: AdapterMeta[] = [
  {
    id: 'xero',
    label: 'Xero',
    description: 'Sync invoices, bills, and bank transactions to Xero.',
    icon: '🟦',
    color: '#13B5EA',
    logoPath: '/bank-logos/xero.svg',
    authType: 'oauth2',
    apiBaseUrl: 'https://api.xero.com/api.xro/2.0',
    authUrl: 'https://login.xero.com/identity/connect/authorize',
    tokenUrl: 'https://identity.xero.com/connect/token',
    scopes: ['accounting.settings', 'accounting.contacts', 'accounting.invoices', 'accounting.payments', 'accounting.banktransactions', 'accounting.manualjournals', 'accounting.attachments', 'offline_access'],
    docsUrl: 'https://developer.xero.com/documentation/getting-started-guide-20',
  },
  {
    id: 'quickbooks',
    label: 'QuickBooks Online',
    description: 'Sync invoices, bills, and payments to QuickBooks Online.',
    icon: '🟢',
    color: '#2CA01C',
    logoPath: '/bank-logos/Intuit Quickbooks.svg',
    authType: 'oauth2',
    // Development keys can only access the sandbox API; production keys use the production API.
    // Override with QBO_API_BASE_URL env var if needed.
    apiBaseUrl: process.env.QBO_API_BASE_URL || (
      process.env.NODE_ENV === 'production' && process.env.QBO_USE_SANDBOX !== 'true'
        ? 'https://quickbooks.api.intuit.com'
        : 'https://sandbox-quickbooks.api.intuit.com'
    ),
    authUrl: 'https://appcenter.intuit.com/connect/oauth2',
    tokenUrl: 'https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer',
    scopes: ['com.intuit.quickbooks.accounting'],
    docsUrl: 'https://developer.intuit.com/app/developer/qbo/docs/develop',
  },
  {
    id: 'sage',
    label: 'Sage Business Cloud',
    description: 'Sync invoices, bills, and payments to Sage Business Cloud Accounting.',
    icon: '🟩',
    color: '#00A651',
    logoPath: '/bank-logos/Sage.svg',
    authType: 'oauth2',
    // Sage Accounting v3.1 REST API. Must match the base URL used by the bank
    // feed strategy (sageBankFeed.ts) and the inbound sync fetchers.
    apiBaseUrl: 'https://api.accounting.sage.com/v3.1',
    // The `filter=apiv3.1` query param is REQUIRED by Sage's auth server to
    // issue tokens for the v3.1 API. Without it, the auth endpoint returns 500.
    authUrl: 'https://www.sageone.com/oauth2/auth/central?filter=apiv3.1',
    tokenUrl: 'https://oauth.accounting.sage.com/token',
    scopes: ['full_access'],
    docsUrl: 'https://developer.sage.com/accounting/',
    // Sage Business Cloud Accounting's OAuth docs do not list PKCE parameters.
    // It uses a confidential client (client_secret) so PKCE is not required.
    supportsPkce: false,
  },
  {
    id: 'sage-za',
    label: 'Sage Business Cloud (ZA)',
    description: 'Sync invoices and bills to Sage Business Cloud Accounting (South Africa).',
    icon: '🟩',
    color: '#00A651',
    logoPath: '/bank-logos/Sage.svg',
    authType: 'api_key',
    // South African Sage Business Cloud Accounting uses a separate API at
    // accounting.sageone.co.za with API Key + Basic Auth (not OAuth).
    // This is a completely different product from the UK/IE/CA v3.1 API.
    apiBaseUrl: 'https://accounting.sageone.co.za/api/2.0.0',
    docsUrl: 'https://www.sage.com/en-za/sage-business-cloud/accounting/developer-api/',
    supportsInboundSync: true,
  },
  {
    id: 'dynamics365',
    label: 'Dynamics 365 Business Central',
    description: 'Sync invoices, bills, and payments to Dynamics 365 Business Central.',
    icon: '🟦',
    color: '#0078D4',
    logoPath: '/bank-logos/Dynamics365_scalable.svg',
    authType: 'oauth2',
    // Business Central API v2.0 — the environment segment (e.g. 'production')
    // is resolved per-tenant at publish time since each tenant can have
    // multiple environments (production, sandbox). The default 'production'
    // environment is used unless the adapter config's tenantId encodes a
    // different environment (format: '{environment}:{companyId}').
    apiBaseUrl: 'https://api.businesscentral.dynamics.com/v2.0/production/api/v2.0',
    // Microsoft Entra ID OAuth2 v2.0 endpoint — /common allows any M365 tenant
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
    // Financials.ReadWrite is the delegated permission for read/write access
    // to BC financial entities (invoices, bills, payments, contacts, accounts).
    // offline_access is required to obtain a refresh token for long-lived access.
    scopes: ['openid', 'offline_access', 'https://api.businesscentral.dynamics.com/Financials.ReadWrite'],
    docsUrl: 'https://learn.microsoft.com/en-us/dynamics365/business-central/dev-itpro/developer/',
    supportsPkce: true,
    supportsInboundSync: true,
  },
];

// --- Publish Preview Implementation ------------------------------------------

/**
 * Generate a publish preview for a document without actually posting it.
 * Shows what will be sent to the target system, including line items, amounts,
 * and any validation issues.
 */
export function generatePublishPreview(
  doc: PublishDocument,
  adapterId?: string,
): PublishPreview {
  const adapter = adapterId
    ? getPublishAdapter(adapterId)
    : Array.from(adapterRegistry.values()).find((a) => a.canPublish(doc));

  const adapterLabel = adapter?.label || 'Unknown';
  const resolvedAdapterId = adapter?.id || adapterId || 'gl';

  const rawLineItems = Array.isArray(doc.payload.lineItems)
    ? doc.payload.lineItems
    : Array.isArray(doc.payload.line_items)
      ? doc.payload.line_items
      : [];

  const lineItems: PublishPreviewLineItem[] = rawLineItems.map((item: Record<string, unknown>) => ({
    description: String(item.description || item.Description || ''),
    quantity: Number(item.quantity || item.Quantity || 1),
    unit_price: Number(item.unit_price || item.UnitAmount || item.amount || 0),
    total: Number(item.total || item.LineAmount || item.line_total || 0),
    account_code: String(item.account_code || item.AccountCode || item.expense_account_code || ''),
    tax_type: String(item.tax_type || item.TaxType || item.tax_code || ''),
  }));

  if (lineItems.length === 0 && doc.payload.total_amount) {
    lineItems.push({
      description: 'Imported expense',
      quantity: 1,
      unit_price: Number(doc.payload.total_amount),
      total: Number(doc.payload.total_amount),
      account_code: String(doc.payload.suggested_account_code || ''),
    });
  }

  const totalAmount = lineItems.reduce((sum, li) => sum + li.total, 0);
  const fx = extractFxContext(doc);
  const warnings: string[] = [];
  const blockingErrors: string[] = [];

  if (!adapter) {
    blockingErrors.push(`No adapter found for id '${resolvedAdapterId}'`);
  } else {
    const configured = adapter.isConfiguredForClient
      ? adapter.isConfiguredForClient(doc.clientId)
      : adapter.isConfigured;
    if (!configured) {
      warnings.push(`Adapter '${adapterLabel}' is not configured`);
    }
  }

  if (!doc.documentId) {
    blockingErrors.push('Document ID is missing');
  }
  if (lineItems.length === 0) {
    blockingErrors.push('No line items to publish');
  }
  if (!doc.payload.vendor_name && !doc.payload.contact_name && doc.workspace === 'costs') {
    warnings.push('No vendor/contact name specified');
  }

  const producedRef = doc.producedRef || {};
  if (producedRef.published_ref) {
    warnings.push('Document appears to already be published');
  }

  const { currency, fallbackUsed } = resolvePublishCurrencyWithWarning(doc);
  if (fallbackUsed) {
    warnings.push('Currency code was missing or invalid — defaulted to ZAR');
  }

  return {
    documentId: doc.documentId,
    workspace: doc.workspace,
    documentType: doc.documentType,
    adapterId: resolvedAdapterId,
    adapterLabel,
    contactName: String(doc.payload.vendor_name || doc.payload.contact_name || 'Unknown'),
    date: String(doc.payload.date || doc.payload.receipt_date || new Date().toISOString().split('T')[0]),
    currency,
    fxRate: fx?.rate ?? null,
    lineItems,
    totalAmount,
    taxAmount: doc.payload.tax_amount ? Number(doc.payload.tax_amount) : undefined,
    warnings,
    canPublish: blockingErrors.length === 0 && Boolean(adapter?.canPublish(doc)),
    blockingErrors,
  };
}

/**
 * Generate previews for multiple documents at once.
 */
export function generateBatchPreview(
  docs: PublishDocument[],
  adapterId?: string,
): PublishPreview[] {
  return docs.map((doc) => generatePublishPreview(doc, adapterId));
}

// --- Batch Publish Implementation --------------------------------------------

/**
 * Publish multiple documents in a batch.
 * Optionally generates previews first and supports stop-on-error.
 */
export async function publishBatch(request: BatchPublishRequest): Promise<BatchPublishResult> {
  const startTime = Date.now();
  const results: BatchPublishResultItem[] = [];
  let succeeded = 0;
  let failed = 0;
  let skipped = 0;

  // If previewFirst is set, generate previews and skip docs that can't publish
  const skippedDocIds = new Set<string>();
  if (request.previewFirst) {
    const previews = generateBatchPreview(request.documents, request.adapterId);
    for (let i = 0; i < previews.length; i++) {
      const preview = previews[i];
      if (!preview.canPublish) {
        skipped++;
        skippedDocIds.add(preview.documentId);
        results.push({
          documentId: preview.documentId,
          success: false,
          skipped: true,
          error: `Skipped: ${preview.blockingErrors.join('; ')}`,
        });
        continue;
      }
    }
  }

  for (let i = 0; i < request.documents.length; i++) {
    const doc = request.documents[i];

    // Skip if already marked as skipped in preview phase
    if (skippedDocIds.has(doc.documentId)) {
      continue;
    }

    const docStart = Date.now();
    const adapter = request.adapterId
      ? getPublishAdapter(request.adapterId)
      : Array.from(adapterRegistry.values()).find((a) => a.canPublish(doc));

    if (!adapter) {
      failed++;
      results.push({
        documentId: doc.documentId,
        success: false,
        error: `No suitable adapter found`,
        durationMs: Date.now() - docStart,
      });
      if (request.stopOnError) break;
      continue;
    }

    try {
      const result = await adapter.publish(doc);
      if (result.success) {
        succeeded++;
      } else {
        failed++;
      }
      results.push({
        documentId: doc.documentId,
        success: result.success,
        externalRef: result.externalRef,
        error: result.error,
        warnings: result.warnings,
        content: result.content,
        contentType: result.contentType,
        filename: result.filename,
        durationMs: Date.now() - docStart,
      });
      if (!result.success && request.stopOnError) break;
    } catch (err) {
      failed++;
      results.push({
        documentId: doc.documentId,
        success: false,
        error: err instanceof Error ? err.message : 'Unknown publish error',
        durationMs: Date.now() - docStart,
      });
      if (request.stopOnError) break;
    }
  }

  return {
    success: failed === 0,
    total: request.documents.length,
    succeeded,
    failed,
    skipped,
    results,
    totalDurationMs: Date.now() - startTime,
  };
}

// --- CSV Export Fallback Adapter (Phase 5) -----------------------------------
//
// For accounting platforms that lack API integration (or when a user prefers
// file-based import), this adapter produces a CSV string that can be downloaded
// and manually imported into any accounting system.

export interface CsvExportRow {
  date: string;
  documentNumber: string;
  type: string;
  contactName: string;
  description: string;
  accountCode: string;
  debit: number;
  credit: number;
  taxAmount: number;
  taxRate: number;
  currency: string;
}

const CSV_HEADERS = ['Date', 'Document Number', 'Type', 'Contact Name', 'Description', 'Account Code', 'Debit', 'Credit', 'Tax Amount', 'Tax Rate', 'Currency'];

/** Escape a value for CSV output (quote if it contains comma, quote, or newline). */
function escapeCsv(val: string | number): string {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Convert an array of CsvExportRow objects into a CSV string. */
function rowsToCsv(rows: CsvExportRow[]): string {
  const lines = [CSV_HEADERS.join(',')];
  for (const row of rows) {
    lines.push([
      escapeCsv(row.date),
      escapeCsv(row.documentNumber),
      escapeCsv(row.type),
      escapeCsv(row.contactName),
      escapeCsv(row.description),
      escapeCsv(row.accountCode),
      escapeCsv(row.debit.toFixed(2)),
      escapeCsv(row.credit.toFixed(2)),
      escapeCsv(row.taxAmount.toFixed(2)),
      escapeCsv(row.taxRate.toFixed(2)),
      escapeCsv(row.currency),
    ].join(','));
  }
  return lines.join('\n');
}

/**
 * Build CsvExportRow[] from a single PublishDocument.
 * Shared by CsvExportAdapter.transformToCsv and generateBatchCsv.
 */
export function buildCsvRows(doc: PublishDocument): CsvExportRow[] {
  const rawLineItems = Array.isArray(doc.payload.lineItems)
    ? doc.payload.lineItems
    : Array.isArray(doc.payload.line_items)
      ? doc.payload.line_items
      : [];

  const rows: CsvExportRow[] = rawLineItems.map((item: Record<string, unknown>) => {
    const total = Number(item.total || item.LineAmount || item.line_total || 0);
    const taxAmount = Number(item.tax_amount || item.TaxAmount || 0);
    const netAmount = total - taxAmount;
    const isSales = doc.workspace === 'sales';
    return {
      date: String(doc.payload.date || doc.payload.receipt_date || new Date().toISOString().split('T')[0]),
      documentNumber: String(doc.documentId || doc.payload.invoice_number || ''),
      type: isSales ? 'Sale' : 'Purchase',
      contactName: String(doc.payload.vendor_name || doc.payload.contact_name || 'Unknown'),
      description: String(item.description || item.Description || ''),
      accountCode: String(item.account_code || item.AccountCode || item.expense_account_code || ''),
      debit: isSales ? 0 : netAmount,
      credit: isSales ? netAmount : 0,
      taxAmount,
      taxRate: Number(item.tax_rate || 0),
      currency: resolvePublishCurrency(doc),
    };
  });

  if (rows.length === 0 && doc.payload.total_amount) {
    const total = Number(doc.payload.total_amount);
    const taxAmount = Number(doc.payload.tax_amount || 0);
    const netAmount = total - taxAmount;
    const isSales = doc.workspace === 'sales';
    rows.push({
      date: String(doc.payload.date || doc.payload.receipt_date || new Date().toISOString().split('T')[0]),
      documentNumber: String(doc.documentId || ''),
      type: isSales ? 'Sale' : 'Purchase',
      contactName: String(doc.payload.vendor_name || doc.payload.contact_name || 'Unknown'),
      description: 'Imported expense',
      accountCode: String(doc.payload.suggested_account_code || ''),
      debit: isSales ? 0 : netAmount,
      credit: isSales ? netAmount : 0,
      taxAmount,
      taxRate: 0,
      currency: resolvePublishCurrency(doc),
    });
  }

  return rows;
}

export class CsvExportAdapter implements PublishAdapter {
  id = 'csv';
  label = 'CSV Export (File-based)';
  isConfigured = true;

  async publish(doc: PublishDocument): Promise<PublishResult> {
    try {
      const csv = this.transformToCsv(doc);
      const externalRef = `csv-${doc.workspace}-${doc.documentId}`;
      const dateStr = String(doc.payload.date || doc.payload.receipt_date || new Date().toISOString().split('T')[0]);
      const filename = `${doc.workspace}-${doc.documentId}-${dateStr}.csv`;
      return {
        success: true,
        externalRef,
        content: csv,
        contentType: 'text/csv',
        filename,
        warnings: ['Document exported as CSV — manual import required'],
      };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'CSV export failed' };
    }
  }

  canPublish(doc: PublishDocument): boolean {
    return Boolean(doc.documentId && doc.clientId);
  }

  private transformToCsv(doc: PublishDocument): string {
    return rowsToCsv(buildCsvRows(doc));
  }
}

// ── Document-type publish dispatch ────────────────────────────────────────────
//
// Every publish call site (approval trigger, auto-publish crons, manual and
// bulk endpoints) must route through dispatchPublishDocument so document types
// reach the right adapter method. Calling adapter.publish() unconditionally
// posts a TA quote as a QBO Invoice (posting AR) instead of an Estimate.

/** Quote/estimate-family document types → adapter.publishQuote (QBO Estimate). */
export const QUOTE_LIKE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'quote', 'estimate', 'sales_quote', 'proposal',
  // A pro-forma invoice is non-posting — QBO's non-posting sales entity is
  // the Estimate, so proformas share the quote path.
  'proforma', 'sales_proforma',
]);

/** Purchase-order-family document types → adapter.publishPurchaseOrder. */
export const PURCHASE_ORDER_LIKE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'purchase_order', 'po', 'purchase_po',
]);

/**
 * Sales-order document types → adapter.publishSalesOrder.
 * Only adapters with a real sales-order entity (currently D365 Business
 * Central) implement it — sales_order stays in INTERNAL_ONLY_DOCUMENT_TYPES
 * for every other provider.
 */
export const SALES_ORDER_LIKE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'sales_order', 'salesorder', 'so',
]);

/** Credit-note document types → adapter.publishCreditNote. */
export const CREDIT_NOTE_LIKE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'supplier_credit', 'credit_note', 'sales_credit', 'customer_credit',
]);

/** Payment document types → adapter.publishPayment. */
export const PAYMENT_LIKE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'payment', 'receipt', 'customer_receipt', 'supplier_payment',
  'bank_payment', 'payment_receipt',
]);

/**
 * Dispatch a document to the adapter method matching its type. Falls back to
 * adapter.publish() for plain invoices/bills/expenses and when the adapter
 * doesn't implement the type-specific method.
 */
export async function dispatchPublishDocument(
  adapter: PublishAdapter,
  doc: PublishDocument,
): Promise<PublishResult> {
  const docType = String(doc.documentType || '').toLowerCase();
  if (PAYMENT_LIKE_DOCUMENT_TYPES.has(docType) && typeof adapter.publishPayment === 'function') {
    return adapter.publishPayment(doc);
  }
  if (CREDIT_NOTE_LIKE_DOCUMENT_TYPES.has(docType) && typeof adapter.publishCreditNote === 'function') {
    return adapter.publishCreditNote(doc);
  }
  if (QUOTE_LIKE_DOCUMENT_TYPES.has(docType) && typeof adapter.publishQuote === 'function') {
    return adapter.publishQuote(doc);
  }
  if (PURCHASE_ORDER_LIKE_DOCUMENT_TYPES.has(docType) && typeof adapter.publishPurchaseOrder === 'function') {
    return adapter.publishPurchaseOrder(doc);
  }
  if (SALES_ORDER_LIKE_DOCUMENT_TYPES.has(docType) && typeof adapter.publishSalesOrder === 'function') {
    return adapter.publishSalesOrder(doc);
  }
  // Safety: an internal-only document type that reaches dispatch with no
  // matching adapter method must never fall through to the generic
  // invoice/bill publish path — it would be posted as the wrong entity.
  if (INTERNAL_ONLY_DOCUMENT_TYPES.has(docType)) {
    return {
      success: false,
      error: `Document type '${doc.documentType}' has no external-accounting equivalent for adapter '${adapter.id}'`,
    };
  }
  return adapter.publish(doc);
}

/**
 * Generate a combined CSV for batch CSV export.
 * Returns a single CSV string with all documents' rows.
 */
export function generateBatchCsv(docs: PublishDocument[]): string {
  const allRows: CsvExportRow[] = docs.flatMap((doc) => buildCsvRows(doc));
  return rowsToCsv(allRows);
}

// Register the GL adapter by default
// Set baseUrl from env so the adapter works server-side (Node.js fetch requires
// absolute URLs). In browser context, client code calls setBaseUrl('') or
// setTokenAccessor which resets to relative URLs.
const _glAdapter = new GLPublishAdapter();
_glAdapter.setBaseUrl(
  process.env.NEXT_PUBLIC_BASE_URL
  || process.env.NEXT_PUBLIC_APP_URL
  || `http://localhost:${process.env.PORT || 4000}`,
);
registerPublishAdapter(_glAdapter);
// Register the CSV export fallback adapter
registerPublishAdapter(new CsvExportAdapter());
